import {
  bindColor,
  brandChartPalette,
  brandTextStyle,
  CHART_KIND_LABELS,
  CHART_KINDS,
  CHART_LABEL_MAX_LENGTH,
  CHART_MAX_LABELS,
  CHART_MAX_SERIES,
  type ChartData,
  type ChartElement,
  type ChartKind,
  type ChartLegendPosition,
  chartColor,
  chartToTable,
  createChartElement,
  isRadialChart,
  parseChartNumber,
  parseChartTable,
  seriesColor,
} from "@cva/shared";
import {
  ChartArea,
  ChartBar,
  ChartColumn,
  ChartLine,
  ChartPie,
  CircleGauge,
  ClipboardPaste,
  Donut,
  Plus,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input, Select, Textarea } from "../components/ui/input";
import { cn } from "../lib/utils";
import { type BrandLink, ColorButton } from "./ColorPicker";
import { addElements } from "./commands";
import { FontPicker } from "./FontPicker";
import { activePageDoc, type EditorStore, useEditor, useEditorApi } from "./store";
import { NumberField, Row, Section, Segmented, Slider, Toggle } from "./ui";

/**
 * Charts: insert tiles in the elements panel, and in the properties panel the
 * kind, a small data grid (labels × series), pasting a table from Excel and
 * the look. Every change is a normal document update (live collaboration and
 * undo work as for any other property).
 */

export const CHART_KIND_ICONS: Record<ChartKind, ReactNode> = {
  column: <ChartColumn />,
  bar: <ChartBar />,
  line: <ChartLine />,
  area: <ChartArea />,
  pie: <ChartPie />,
  donut: <Donut />,
  progress: <CircleGauge />,
};

const INSERT_TITLES: Partial<Record<ChartKind, string>> = {
  column: "Säulendiagramm einfügen",
  bar: "Balkendiagramm einfügen",
  line: "Liniendiagramm einfügen",
  pie: "Kreisdiagramm einfügen",
  donut: "Ringdiagramm einfügen",
  progress: "Fortschrittsring (Kennzahl) einfügen",
};
const INSERT_KINDS = Object.keys(INSERT_TITLES) as ChartKind[];

/**
 * Adds a chart with sample data, centred, about two thirds of the page wide.
 * With a brand kit: brand colours for the series, text colour and body font.
 */
export function addChart(store: EditorStore, kind: ChartKind): string | null {
  const { brand, readOnly } = store.getState();
  const doc = activePageDoc(store);
  if (readOnly) return null;
  const square = kind === "pie" || kind === "donut" || kind === "progress";
  const short = Math.min(doc.format.width, doc.format.height);
  const width = Math.round(square ? short * 0.55 : Math.min(doc.format.width * 0.7, short * 1.1));
  const height = Math.round(square ? (kind === "progress" ? width : width * 1.05) : width * 0.62);
  const fontSize = Math.max(8, Math.round(Math.min(width, height) / 20));
  const element = createChartElement(kind, {
    x: (doc.format.width - width) / 2,
    y: (doc.format.height - height) / 2,
    width,
    height,
    fontSize,
    lineWidth: Math.max(1.5, Math.round(fontSize / 5)),
    barRadius: Math.round(fontSize / 3),
  });
  if (brand) {
    const palette = brandChartPalette(brand);
    if (palette) {
      element.palette = palette;
      element.brandPalette = true;
    }
    if (!bindColor(element, "color", "text", brand)) bindColor(element, "color", "primary", brand);
    const body = brandTextStyle(brand, "body");
    if (body) element.fontFamily = body.fontFamily;
  }
  addElements(store, [element]);
  return element.id;
}

/** „Diagramm“ in the elements panel: one tile per kind. */
export function ChartInsert() {
  const store = useEditorApi();
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {INSERT_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            title={INSERT_TITLES[kind]}
            onClick={() => addChart(store, kind)}
            className="flex flex-col items-center justify-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 py-2.5 text-[11px] text-zinc-600 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 [&_svg]:size-6"
          >
            {CHART_KIND_ICONS[kind]}
            {CHART_KIND_LABELS[kind]}
          </button>
        ))}
      </div>
    </div>
  );
}

/** A number as people type it here: decimal comma. */
function numberText(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : String(value).replace(".", ",");
}

/** Text cell that commits on every change (one undo step per cell and focus). */
function Cell({
  value,
  onChange,
  onPasteTable,
  numeric,
  placeholder,
  label,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  onPasteTable: (text: string) => boolean;
  numeric?: boolean;
  placeholder?: string;
  label: string;
  className?: string;
}) {
  const [text, setText] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(value);
  }, [value, focused]);
  return (
    <input
      value={text}
      aria-label={label}
      placeholder={placeholder}
      inputMode={numeric ? "decimal" : undefined}
      maxLength={CHART_LABEL_MAX_LENGTH}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setText(value);
      }}
      onChange={(event) => {
        setText(event.target.value);
        onChange(event.target.value);
      }}
      onPaste={(event) => {
        const pasted = event.clipboardData.getData("text/plain");
        if (/[\t\n]/.test(pasted.trim()) && onPasteTable(pasted)) event.preventDefault();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") (event.target as HTMLInputElement).blur();
      }}
      className={cn(
        "h-7 w-full min-w-0 rounded border border-transparent bg-transparent px-1.5 text-xs outline-none hover:border-zinc-200 focus:border-brand-400 focus:bg-white",
        numeric && "text-right tabular-nums",
        className,
      )}
    />
  );
}

export function ChartProperties({
  element,
  colorLink,
}: {
  element: ChartElement;
  colorLink?: BrandLink | undefined;
}) {
  const store = useEditorApi();
  const hasBrand = useEditor((s) => s.brand !== null);
  const [pasting, setPasting] = useState(false);
  const [table, setTable] = useState("");
  const set = (recipe: (target: ChartElement) => void, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.elements[element.id];
        if (target?.type === "chart" && !target.locked) recipe(target);
      },
      merge ? { merge: `chart-${element.id}-${merge}` } : undefined,
    );
  const radial = isRadialChart(element.kind);
  const shownSeries = radial ? element.series.slice(0, 1) : element.series;
  const rows = element.kind === "progress" ? 1 : element.labels.length;

  const applyData = (data: ChartData) =>
    set((target) => {
      target.labels = data.labels;
      target.series = data.series.map((series, index) => {
        const color = target.series[index]?.color;
        return { ...series, ...(color ? { color } : {}) };
      });
    });
  const pasteTable = (text: string): boolean => {
    const data = parseChartTable(text);
    if (!data) {
      toast.error("In der Tabelle wurden keine Zahlen gefunden.");
      return false;
    }
    applyData(data);
    toast.success(
      `${data.labels.length} ${data.labels.length === 1 ? "Zeile" : "Zeilen"}, ${data.series.length} ${data.series.length === 1 ? "Reihe" : "Reihen"} übernommen`,
    );
    return true;
  };
  const setValue = (seriesIndex: number, row: number, text: string) => {
    const value = text.trim() === "" ? null : parseChartNumber(text);
    if (text.trim() !== "" && value === null) return;
    set((target) => {
      const series = target.series[seriesIndex];
      if (!series) return;
      while (series.values.length < target.labels.length) series.values.push(null);
      series.values[row] = value;
    }, `value-${seriesIndex}-${row}`);
  };
  const setSliceColor = (index: number, color: string) =>
    set((target) => {
      const palette = Array.from(
        { length: Math.max(index + 1, target.palette?.length ?? 0, target.labels.length) },
        (_, i) => chartColor(target, i),
      );
      palette[index] = color;
      target.palette = palette.slice(0, 12);
      target.brandPalette = false;
    });
  const toggleBrandPalette = (on: boolean) => {
    const brand = store.getState().brand;
    set((target) => {
      target.brandPalette = on;
      const palette = on && brand ? brandChartPalette(brand) : null;
      if (palette) target.palette = palette;
      else if (!on) delete target.palette;
      if (on) for (const series of target.series) delete series.color;
    });
  };

  return (
    <>
      <Section title="Diagramm">
        <div className="grid grid-cols-7 gap-1">
          {CHART_KINDS.map((kind) => (
            <button
              key={kind}
              type="button"
              title={CHART_KIND_LABELS[kind]}
              aria-label={CHART_KIND_LABELS[kind]}
              aria-pressed={element.kind === kind}
              onClick={() =>
                set((target) => {
                  target.kind = kind;
                  if (kind === "progress" && target.labels.length === 0) target.labels = [""];
                })
              }
              className={cn(
                "flex aspect-square items-center justify-center rounded-md border text-zinc-600 [&_svg]:size-4.5",
                element.kind === kind
                  ? "border-brand-500 bg-brand-50 text-brand-700"
                  : "border-zinc-200 hover:border-brand-300",
              )}
            >
              {CHART_KIND_ICONS[kind]}
            </button>
          ))}
        </div>
        {element.kind !== "progress" ? (
          <Input
            className="h-8 text-sm"
            placeholder="Titel (optional)"
            value={element.title}
            maxLength={200}
            onChange={(event) => set((target) => (target.title = event.target.value), "title")}
            aria-label="Titel des Diagramms"
          />
        ) : null}
      </Section>

      <Section
        title="Daten"
        actions={
          <button
            type="button"
            onClick={() => {
              setTable(chartToTable(element));
              setPasting((open) => !open);
            }}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline [&_svg]:size-3.5"
            title="Eine Tabelle aus Excel, Numbers oder Google Sheets einfügen"
          >
            <ClipboardPaste /> Aus Excel/Tabelle einfügen
          </button>
        }
      >
        {pasting ? (
          <div className="space-y-2 rounded-lg bg-zinc-50 p-2">
            <Textarea
              value={table}
              onChange={(event) => setTable(event.target.value)}
              rows={6}
              className="font-mono text-xs"
              placeholder={"\tUmsatz\tKosten\nQ1\t12,5\t8\nQ2\t14\t9,5"}
              aria-label="Tabelle zum Einfügen"
            />
            <p className="text-[11px] text-zinc-500">
              Zellen aus Excel kopieren und hier einfügen – getrennt durch Tab, Semikolon oder
              Komma. Erste Zeile: Namen der Reihen, erste Spalte: Beschriftungen. Dezimalkomma
              (12,5) wird erkannt.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="h-7 rounded-md px-2.5 text-xs text-zinc-600 hover:bg-zinc-200"
                onClick={() => setPasting(false)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="h-7 rounded-md bg-brand-600 px-2.5 text-xs font-medium text-white hover:bg-brand-700"
                onClick={() => {
                  if (pasteTable(table)) setPasting(false);
                }}
              >
                Übernehmen
              </button>
            </div>
          </div>
        ) : null}
        <div className="-mx-1 overflow-x-auto">
          <table className="w-full border-separate border-spacing-0 text-xs">
            <thead>
              <tr>
                <th className="w-24 min-w-20" />
                {shownSeries.map((series, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: series have no id of their own
                  <th key={index} className="min-w-24 px-0.5 pb-1 font-normal">
                    <div className="flex items-center gap-0.5">
                      {radial ? null : (
                        <ColorButton
                          label={`Farbe von ${series.name || `Reihe ${index + 1}`}`}
                          value={seriesColor(element, series, index)}
                          onChange={(color) =>
                            set((target) => {
                              const entry = target.series[index];
                              if (entry) entry.color = color;
                            })
                          }
                        >
                          <span className="sr-only">Farbe</span>
                        </ColorButton>
                      )}
                      <Cell
                        value={series.name}
                        label={`Name der Reihe ${index + 1}`}
                        placeholder={`Reihe ${index + 1}`}
                        className="min-w-12 font-medium"
                        onPasteTable={pasteTable}
                        onChange={(name) =>
                          set((target) => {
                            const entry = target.series[index];
                            if (entry) entry.name = name;
                          }, `series-${index}`)
                        }
                      />
                      {!radial && element.series.length > 1 ? (
                        <button
                          type="button"
                          title="Reihe entfernen"
                          className="shrink-0 rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                          onClick={() => set((target) => void target.series.splice(index, 1))}
                        >
                          <X className="size-3" />
                        </button>
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rows }, (_, row) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: rows are positions
                <tr key={row} className="group">
                  <td className="pr-0.5">
                    <div className="flex items-center gap-0.5">
                      {radial && element.kind !== "progress" ? (
                        <ColorButton
                          label={`Farbe von ${element.labels[row] || `Zeile ${row + 1}`}`}
                          value={chartColor(element, row)}
                          onChange={(color) => setSliceColor(row, color)}
                        >
                          <span className="sr-only">Farbe</span>
                        </ColorButton>
                      ) : null}
                      <Cell
                        value={element.labels[row] ?? ""}
                        label={`Beschriftung ${row + 1}`}
                        placeholder={
                          element.kind === "progress" ? "Beschriftung" : `Zeile ${row + 1}`
                        }
                        onPasteTable={pasteTable}
                        onChange={(label) =>
                          set((target) => {
                            while (target.labels.length <= row) target.labels.push("");
                            target.labels[row] = label;
                          }, `label-${row}`)
                        }
                      />
                    </div>
                  </td>
                  {shownSeries.map((series, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: series have no id of their own
                    <td key={index} className="px-0.5">
                      <Cell
                        numeric
                        value={numberText(series.values[row])}
                        label={`Wert ${row + 1} von ${series.name || `Reihe ${index + 1}`}`}
                        onPasteTable={pasteTable}
                        onChange={(text) => setValue(index, row, text)}
                      />
                    </td>
                  ))}
                  <td className="w-5">
                    {element.kind !== "progress" && element.labels.length > 1 ? (
                      <button
                        type="button"
                        title="Zeile entfernen"
                        className="rounded p-0.5 text-zinc-300 opacity-0 group-hover:opacity-100 hover:bg-zinc-100 hover:text-zinc-700 focus:opacity-100"
                        onClick={() =>
                          set((target) => {
                            target.labels.splice(row, 1);
                            for (const series of target.series) series.values.splice(row, 1);
                          })
                        }
                      >
                        <X className="size-3" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {element.kind !== "progress" ? (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={element.labels.length >= CHART_MAX_LABELS}
              onClick={() =>
                set((target) => {
                  target.labels.push("");
                  for (const series of target.series) {
                    while (series.values.length < target.labels.length - 1)
                      series.values.push(null);
                    series.values.push(null);
                  }
                })
              }
              className="inline-flex h-7 flex-1 items-center justify-center gap-1 rounded-md border border-zinc-200 text-xs hover:border-brand-300 disabled:opacity-40 [&_svg]:size-3.5"
            >
              <Plus /> Zeile
            </button>
            {radial ? null : (
              <button
                type="button"
                disabled={element.series.length >= CHART_MAX_SERIES}
                onClick={() =>
                  set(
                    (target) =>
                      void target.series.push({
                        name: `Reihe ${target.series.length + 1}`,
                        values: target.labels.map(() => null),
                      }),
                  )
                }
                className="inline-flex h-7 flex-1 items-center justify-center gap-1 rounded-md border border-zinc-200 text-xs hover:border-brand-300 disabled:opacity-40 [&_svg]:size-3.5"
              >
                <Plus /> Reihe
              </button>
            )}
          </div>
        ) : null}
        {radial && element.kind !== "progress" && element.series.length > 1 ? (
          <p className="text-[11px] text-zinc-500">
            Kreis und Ring zeigen die erste Reihe; die übrigen bleiben für Säulen und Linien
            erhalten.
          </p>
        ) : null}
      </Section>

      <Section title="Darstellung">
        <Row>
          <span className="w-20 shrink-0 text-xs text-zinc-500">Text</span>
          <ColorButton
            value={element.color}
            onChange={(color) => set((target) => (target.color = color))}
            brandLink={colorLink}
          />
        </Row>
        <FontPicker
          value={element.fontFamily}
          onChange={(family) => set((target) => (target.fontFamily = family))}
        />
        {hasBrand ? (
          <Toggle
            checked={element.brandPalette}
            onChange={toggleBrandPalette}
            label="Farben der Marke"
            description="Reihen in Primär-, Sekundär- und Akzentfarbe; folgt dem Brand Kit."
          />
        ) : null}
        {element.kind !== "progress" ? (
          <Row>
            <span className="w-20 shrink-0 text-xs text-zinc-500">Legende</span>
            <Select
              className="h-8 flex-1 text-xs"
              aria-label="Legende"
              value={element.legend}
              onChange={(event) =>
                set((target) => (target.legend = event.target.value as ChartLegendPosition))
              }
            >
              <option value="none">Keine</option>
              <option value="top">Oben</option>
              <option value="bottom">Unten</option>
              <option value="right">Rechts</option>
            </Select>
          </Row>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="Schrift"
            unit="px"
            value={element.fontSize}
            min={4}
            max={400}
            onChange={(v) => set((target) => (target.fontSize = v), "font-size")}
          />
          <NumberField
            label="Stellen"
            value={element.decimals}
            min={0}
            max={4}
            onChange={(v) =>
              set((target) => (target.decimals = Math.max(0, Math.min(4, Math.round(v)))))
            }
          />
        </div>
        <Row>
          <span className="w-20 shrink-0 text-xs text-zinc-500">Einheit</span>
          <Input
            className="h-8 flex-1 text-sm"
            value={element.unit}
            maxLength={12}
            placeholder="z. B. % oder €"
            onChange={(event) => set((target) => (target.unit = event.target.value), "unit")}
            aria-label="Einheit"
          />
        </Row>
        {element.kind === "progress" ? (
          <NumberField
            label="Ziel"
            value={element.max ?? 100}
            min={0.0001}
            precision={2}
            onChange={(v) => set((target) => (target.max = v > 0 ? v : 100), "max")}
          />
        ) : null}
        {element.kind === "donut" || element.kind === "progress" ? (
          <Row>
            <span className="w-20 shrink-0 text-xs text-zinc-500">Loch</span>
            <Slider
              value={Math.round(element.hole * 100)}
              min={20}
              max={90}
              onChange={(v) => set((target) => (target.hole = v / 100), "hole")}
            />
          </Row>
        ) : null}
        {element.kind === "column" || element.kind === "bar" ? (
          <NumberField
            label="Ecken"
            unit="px"
            value={element.barRadius}
            min={0}
            max={500}
            onChange={(v) => set((target) => (target.barRadius = Math.max(0, v)), "radius")}
          />
        ) : null}
        {element.kind === "line" || element.kind === "area" ? (
          <>
            <NumberField
              label="Linie"
              unit="px"
              value={element.lineWidth}
              min={0.5}
              max={60}
              precision={1}
              onChange={(v) => set((target) => (target.lineWidth = v), "line-width")}
            />
            <Segmented
              className="w-full"
              value={element.smooth ? "smooth" : "straight"}
              onChange={(value) => set((target) => (target.smooth = value === "smooth"))}
              options={[
                { value: "smooth", label: "Geschwungen", title: "Weiche Kurven durch die Punkte" },
                { value: "straight", label: "Gerade", title: "Gerade Linien zwischen den Punkten" },
              ]}
            />
            <Toggle
              checked={element.points}
              onChange={(checked) => set((target) => (target.points = checked))}
              label="Punkte zeigen"
            />
          </>
        ) : null}
        {element.kind === "column" || element.kind === "bar" || element.kind === "area" ? (
          <Toggle
            checked={element.stacked}
            onChange={(checked) => set((target) => (target.stacked = checked))}
            label="Gestapelt"
            description="Reihen übereinander statt nebeneinander."
          />
        ) : null}
        {element.kind !== "progress" ? (
          <Toggle
            checked={element.showValues}
            onChange={(checked) => set((target) => (target.showValues = checked))}
            label="Werte anzeigen"
          />
        ) : null}
        {!radial ? (
          <>
            <Toggle
              checked={element.gridlines}
              onChange={(checked) => set((target) => (target.gridlines = checked))}
              label="Gitternetz"
            />
            <Toggle
              checked={element.axis}
              onChange={(checked) => set((target) => (target.axis = checked))}
              label="Achsenbeschriftung"
            />
          </>
        ) : null}
      </Section>
    </>
  );
}
